import { Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { APP_BASE } from './constants/config';
import lazyWithRetry from './utils/lazyWithRetry';
import Layout from './components/Layout';
import QuizLayout from './components/QuizLayout';
import SuspenseWrapper from './components/SuspenseWrapper';
import SentryRouteTracker from './components/SentryRouteTracker';

const HomePage = lazyWithRetry(() => import('./components/HomePage'));
const OfferOverlayGroup = lazyWithRetry(
  () => import('./components/OfferOverlayGroup')
);
const LoginPromptOverlay = lazyWithRetry(
  () => import('./components/LoginPromptOverlay')
);
const SearchOverlay = lazyWithRetry(() => import('./components/SearchOverlay'));
const SearchResultsPage = lazyWithRetry(
  () => import('./components/SearchResultsPage')
);
const OffersPage = lazyWithRetry(() => import('./components/OffersPage'));
const MapPage = lazyWithRetry(() => import('./components/MapPage'));
const MapCategoryPickerOverlay = lazyWithRetry(
  () => import('./components/MapCategoryPickerOverlay')
);
const MapPartnerIdOverlay = lazyWithRetry(
  () => import('./components/MapPartnerIdOverlay')
);
const PartnerOfferOverlay = lazyWithRetry(
  () => import('./components/PartnerOfferOverlay')
);
const CouponsPage = lazyWithRetry(() => import('./components/CouponsPage'));
const YettelPetak = lazyWithRetry(() => import('./components/YettelPetak'));
const YettelPetakHistory = lazyWithRetry(
  () => import('./components/YettelPetakHistory')
);
const ProfilePage = lazyWithRetry(() => import('./components/ProfilePage'));
const ProfileSettings = lazyWithRetry(
  () => import('./components/ProfileSettings')
);
const SendSuggestions = lazyWithRetry(
  () => import('./components/SendSuggestions')
);
const SuggestionSent = lazyWithRetry(
  () => import('./components/SuggestionSent')
);
const PrivacySettings = lazyWithRetry(
  () => import('./components/PrivacySettings')
);
const LoginRedirectPage = lazyWithRetry(
  () => import('./components/LoginRedirectPage')
);
const MapCategoryAndPartnerPickerOverlay = lazyWithRetry(
  () => import('./components/MapCategoryAndPartnerPickerOverlay')
);
const ProfileInterests = lazyWithRetry(
  () => import('./components/ProfileInterests')
);
const SpecialPrizePage = lazyWithRetry(
  () => import('./components/SpecialPrizePage')
);
const HamburgerMenuPopup = lazyWithRetry(
  () => import('./components/HamburgerMenuPopup')
);
const AuthGuard = lazyWithRetry(() => import('./components/AuthGuard'));
const MauGameCouponList = lazyWithRetry(
  () => import('./components/MauGameCouponList')
);
const QuizStartPageContainer = lazyWithRetry(
  () => import('./components/QuizStartPageContainer')
);
const QuizResultPage = lazyWithRetry(
  () => import('./components/QuizResultPage')
);
const QuizQuestionsContainer = lazyWithRetry(
  () => import('./components/QuizQuestions')
);
const QuizDoubleBind = lazyWithRetry(
  () => import('./components/QuizDoubleBind')
);
const ErrorPageContent = lazyWithRetry(
  () => import('./components/ErrorPageContent')
);

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <>
          <SentryRouteTracker />
          <Layout />
          <Suspense fallback={null}>
            <SearchOverlay />
            <HamburgerMenuPopup />
          </Suspense>
        </>
      ),
      errorElement: (
        <Layout>
          <ErrorPageContent />
        </Layout>
      ),
      children: [
        {
          path: '/',
          element: (
            <>
              <Outlet />
              <Suspense fallback={null}>
                <OfferOverlayGroup />
                <LoginPromptOverlay />
              </Suspense>
            </>
          ),
          errorElement: <ErrorPageContent />,
          children: [
            {
              path: '/',
              element: (
                <SuspenseWrapper>
                  <HomePage />
                </SuspenseWrapper>
              )
            },
            {
              path: '/pretraga/:searchTerm',
              element: (
                <SuspenseWrapper>
                  <SearchResultsPage />
                </SuspenseWrapper>
              )
            },
            {
              path: '/ponude',
              element: (
                <SuspenseWrapper>
                  <OffersPage />
                </SuspenseWrapper>
              )
            },
            {
              path: '/be80c6d202321c80cecefd9022ecdf25',
              element: (
                <SuspenseWrapper>
                  <MauGameCouponList />
                </SuspenseWrapper>
              )
            }
          ]
        },
        {
          path: '/near-by',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <MapPage />
              <OfferOverlayGroup />
              <Outlet />
            </SuspenseWrapper>
          ),
          children: [
            {
              path: '/near-by/categories',
              element: (
                <SuspenseWrapper>
                  <MapCategoryPickerOverlay />
                </SuspenseWrapper>
              )
            },
            {
              path: '/near-by/filters',
              element: (
                <SuspenseWrapper>
                  <MapCategoryAndPartnerPickerOverlay />
                </SuspenseWrapper>
              )
            },
            {
              path: '/near-by/partner/:partnerId',
              element: <Outlet />,
              children: [
                {
                  path: '/near-by/partner/:partnerId',
                  element: (
                    <SuspenseWrapper>
                      <MapPartnerIdOverlay />
                      <Outlet />
                    </SuspenseWrapper>
                  ),
                  children: [
                    {
                      path: '/near-by/partner/:partnerId/offer/:offerId',
                      element: (
                        <SuspenseWrapper>
                          <PartnerOfferOverlay />
                        </SuspenseWrapper>
                      )
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: '/kuponi',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <AuthGuard text="Prijavi se kako bi video svoje sačuvane i istekle kupone.">
                <CouponsPage />
              </AuthGuard>
            </SuspenseWrapper>
          )
        },
        {
          path: '/yettel-petak',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <YettelPetak />
            </SuspenseWrapper>
          )
        },
        {
          path: '/pregled-dobitaka',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <YettelPetakHistory />
            </SuspenseWrapper>
          )
        },
        {
          path: '/profil',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <AuthGuard>
                <Outlet />
              </AuthGuard>
            </SuspenseWrapper>
          ),
          children: [
            {
              path: '/profil',
              element: (
                <SuspenseWrapper>
                  <ProfilePage />
                </SuspenseWrapper>
              )
            },
            {
              path: '/profil/podesavanja',
              element: (
                <SuspenseWrapper>
                  <ProfileSettings />
                </SuspenseWrapper>
              )
            },
            {
              path: '/profil/interesovanja',
              element: (
                <SuspenseWrapper>
                  <ProfileInterests />
                </SuspenseWrapper>
              )
            },
            {
              path: '/profil/sugestije',
              element: <Outlet />,
              children: [
                {
                  path: '/profil/sugestije',
                  element: (
                    <SuspenseWrapper>
                      <SendSuggestions />
                    </SuspenseWrapper>
                  )
                },
                {
                  path: '/profil/sugestije/poslato',
                  element: (
                    <SuspenseWrapper>
                      <SuggestionSent />
                    </SuspenseWrapper>
                  )
                }
              ]
            },
            {
              path: '/profil/privatnost',
              element: (
                <SuspenseWrapper>
                  <PrivacySettings />
                </SuspenseWrapper>
              )
            }
          ]
        },
        {
          path: '/quiz',
          element: <QuizLayout />,
          errorElement: <ErrorPageContent />,
          children: [
            {
              path: '/quiz',
              element: (
                <SuspenseWrapper>
                  <QuizStartPageContainer />
                </SuspenseWrapper>
              )
            },
            {
              path: '/quiz/play',
              element: (
                <SuspenseWrapper>
                  <QuizQuestionsContainer />
                </SuspenseWrapper>
              )
            },
            {
              path: '/quiz/result',
              element: (
                <SuspenseWrapper>
                  <QuizResultPage />
                </SuspenseWrapper>
              )
            },
            {
              path: '/quiz/double-bind',
              element: (
                <SuspenseWrapper>
                  <QuizDoubleBind />
                </SuspenseWrapper>
              )
            }
          ]
        },
        {
          path: '/special',
          element: (
            <>
              <SuspenseWrapper>
                <SpecialPrizePage />
              </SuspenseWrapper>
              <Suspense fallback={null}>
                <OfferOverlayGroup />
                <LoginPromptOverlay />
              </Suspense>
            </>
          )
        },
        {
          path: '/login-redirect',
          errorElement: <ErrorPageContent />,
          element: (
            <SuspenseWrapper>
              <LoginRedirectPage />
            </SuspenseWrapper>
          )
        },
        {
          path: '/greska-prilikom-autorizacije',
          element: (
            <ErrorPageContent
              customMessage="Nismo uspeli da proverimo tvoje podatke. Molimo te da pokušaš da se uloguješ ponovo."
              showLoginButton={true}
            />
          )
        },
        {
          path: '/greska-tokom-sso-procesa',
          element: (
            <ErrorPageContent
              customMessage="Nismo uspeli da te automatski ulogujemo na Yettel Shopping aplikaciji. Molimo te da se sam uloguješ, klikom na dugme Prijavi se."
              showLoginButton={true}
            />
          )
        },
        {
          path: '*',
          element: <ErrorPageContent />
        }
      ]
    }
  ],
  {
    basename: APP_BASE
  }
);

export default router;
